// CORE
import * as React from 'react';
import { useContext } from 'react';
import { useSelector } from 'react-redux';

import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
// MUI
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

import { goalsSelectors } from 'app-components/flux/goals';

// SHARED/LOCAL
import type { specificGoalType } from './state';
import { AddGoalContext, goalTypes } from './state';

const useStyles = makeStyles<{ spendingPlan?: boolean }>()((theme, { spendingPlan }) => ({
  typeButton: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius * 4,
    padding: theme.spacing(2),
    margin: spendingPlan ? theme.spacing(1) : theme.spacing(2),
    boxShadow: spendingPlan ? theme.shadows[0] : theme.shadows[2],
    border: `1px solid ${theme.palette.border.light}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    cursor: 'pointer',
    transition: 'transform 300ms ease-in-out, box-shadow 300ms ease-in-out, border 300ms ease-in-out, ',
    '&:hover, &:focus': {
      transform: 'translate(0px, 0px)',
      boxShadow: `0px 0px 0px 4px ${theme.palette.primary.light}`,
      border: `1px solid ${theme.palette.primary.main}`,
    },
    '& > img': {
      height: 100,
      width: 'auto',
    },
  },
  buttonTitle: {},
  header: {
    margin: theme.spacing(1),
  },
  textItems: {
    justifyContent: 'center',
    textAlign: 'center',
    width: 500,
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(5),
  },
  gridRoot: {
    width: spendingPlan ? 750 : '100%',
    marginBottom: spendingPlan ? theme.spacing(4) : 0,
  },
}));

const GoalPicker = ({
  spendingPlan,
  openGoalFunc,
}: {
  spendingPlan?: boolean;
  openGoalFunc?: (name: string, type: specificGoalType) => void;
}) => {
  const { dispatch } = useContext(AddGoalContext);
  const { classes } = useStyles({ spendingPlan });

  const goalsById: any = useSelector(goalsSelectors.getResourcesById);

  const handleSelect = (type: specificGoalType) => () => {
    // type is the entire object defined in goalTypes in ./state.js
    let name = type.name.slice();
    let counter = 1;
    // @ts-expect-error FIXME TS7006
    const isRepeatName = (goal) => goal.name === name; // for lint
    while (goalsById.find(isRepeatName)) {
      name = `${type.name} ${counter}`;
      counter += 1;
    }
    if (spendingPlan && openGoalFunc) {
      openGoalFunc(name, type);
    } else {
      dispatch({ type: 'update', payload: { type, tabIndex: 1, name } });
    }
  };

  const handleKeySelect =
    (type: specificGoalType) =>
    (event: React.KeyboardEvent): void => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleSelect(type)();
      }
    };

  return (
    <Box
      display={'flex'}
      alignItems={'center'}
      flexDirection={'column'}
      data-testid={spendingPlan ? 'sp-goals-zero-state' : 'goals-zero-state'}
    >
      {!spendingPlan ? (
        <Typography className={classes.header} variant='headlineMedium'>
          What are you saving for?
        </Typography>
      ) : (
        <div className={classes.textItems}>
          <Typography variant='titleMedium'>Create a savings goal</Typography>
          <Typography variant='paragraphRegular'>
            Contribute any remaining funds after Bills and Spending to a Savings Goal. Plan for a vacation, buying a
            home, or anything in between!
          </Typography>
        </div>
      )}
      <Grid container className={classes.gridRoot}>
        {goalTypes.map((gt, _index) => (
          <Grid
            id={`create_goal_type_${gt.type}`}
            key={`create-goal-type-${gt.type}`}
            item
            xs={spendingPlan ? 4 : 6}
            md={4}
            lg={4}
          >
            <Box
              onClick={handleSelect(gt)}
              onKeyDown={handleKeySelect(gt)}
              role='button'
              aria-label={gt.name}
              tabIndex={0}
              className={classes.typeButton}
            >
              <img src={gt.icon} alt={gt.name} />
              <Typography variant='titleRegular' className={classes.buttonTitle}>
                {gt.name}
              </Typography>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default GoalPicker;
